import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import Layout from "components/Layout";
import api from "utils/api";
import { buildRegistrationPayload } from "utils/registration";
import "styles/karriere.css";

const EMPLOYMENT_MODELS = [
  {
    title: "Minijob",
    text:
      "Du möchtest flexibel arbeiten und suchst eine sinnvolle Tätigkeit im Gesundheitsumfeld? Dann kann ein Minijob bei Dr. Hoffmann genau das Richtige sein. Wir bieten flexible Einsatzmöglichkeiten, klare Aufgaben und ein professionelles Umfeld.",
  },
  {
    title: "Teilzeit",
    text:
      "Für alle, die Beruf, Familie oder andere Verpflichtungen miteinander verbinden möchten, bieten wir Teilzeitmodelle mit planbaren Strukturen. Bei uns kannst du dich einbringen, ohne auf Flexibilität verzichten zu müssen.",
  },
  {
    title: "Festanstellung",
    text:
      "Du suchst eine langfristige Perspektive in einem wachsenden Gesundheitskonzept? In Festanstellung bieten wir dir die Möglichkeit, aktiv am Aufbau und an der Weiterentwicklung von Dr. Hoffmann mitzuwirken.",
  },
  {
    title: "Lizenznehmer",
    text:
      "Du möchtest unternehmerisch arbeiten und Teil eines skalierbaren Gesundheitsmodells werden? Als Lizenznehmer oder Partner kannst du mit Dr. Hoffmann ein eigenes regionales Konzept umsetzen und von einem strukturierten System profitieren.",
  },
];

const PROFILE_POINTS = [
  "Verantwortungsbewusstsein",
  "Zuverlässigkeit",
  "freundliches und professionelles Auftreten",
  "Interesse am Gesundheitswesen",
  "Organisationstalent",
  "Kommunikationsstärke",
  "Bereitschaft, nach klaren Standards zu arbeiten",
  "Motivation, gemeinsam etwas aufzubauen",
];

const EXPERIENCE_AREAS = [
  "medizinische Assistenz",
  "Pflege",
  "Praxisorganisation",
  "Patientenbetreuung",
  "Gesundheitsdienstleistungen",
  "Verwaltung",
  "Außendienst",
  "regionale Partner und Unternehmer",
];

const BENEFITS = [
  "ein modernes Gesundheitskonzept",
  "flexible Beschäftigungsmodelle",
  "klare Strukturen",
  "ein wachsendes Netzwerk",
  "verantwortungsvolle Aufgaben",
  "kurze Entscheidungswege",
  "Perspektiven zur Weiterentwicklung",
  "die Möglichkeit, aktiv am Aufbau eines skalierbaren Systems mitzuwirken",
];

const MODEL_OPTIONS = [
  "Minijob",
  "Teilzeit",
  "Festanstellung",
  "Lizenznehmer / Partner",
];

const KarrierePage = () => {
  const location = useLocation();
  const tracking = useMemo(() => buildRegistrationPayload(location), [location]);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    desiredModel: "Minijob",
    message: "",
    acceptedContact: false,
  });
  const [documents, setDocuments] = useState([]);
  const [documentInputKey, setDocumentInputKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const updateForm = (key) => (event) => {
    const value =
      event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setForm((current) => ({ ...current, [key]: value }));
  };

  const updateDocuments = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    setDocuments((current) => {
      const merged = [...current];
      selectedFiles.forEach((file) => {
        const alreadySelected = merged.some(
          (item) => item.name === file.name && item.size === file.size
        );
        if (!alreadySelected && merged.length < 3) merged.push(file);
      });
      return merged;
    });
    setDocumentInputKey((current) => current + 1);
  };

  const removeDocument = (fileToRemove) => {
    setDocuments((current) =>
      current.filter(
        (file) => !(file.name === fileToRemove.name && file.size === fileToRemove.size)
      )
    );
  };

  const submitToBackend = async (payload) => {
    if (payload.documents?.length) {
      const formData = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        if (key === "documents" || value === undefined || value === null) return;
        if (Array.isArray(value)) {
          value.forEach((item) => formData.append(`${key}[]`, item));
          return;
        }
        if (typeof value === "object") {
          formData.append(key, JSON.stringify(value));
          return;
        }
        formData.append(key, value);
      });
      payload.documents.forEach((file) => formData.append("documents", file));

      try {
        return await api.post("/inquiries", formData);
      } catch (err) {
        const status = err?.response?.status;
        if (status && status !== 404 && status !== 405) throw err;
        return api.post("/special-requests", formData);
      }
    }

    try {
      return await api.post("/inquiries", payload);
    } catch (err) {
      const status = err?.response?.status;
      if (status && status !== 404 && status !== 405) throw err;
      return api.post("/special-requests", payload);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setShowSuccess(false);

    if (!form.acceptedContact) {
      setError("Bitte bestätige den Kontakt- und Datenschutzhinweis.");
      return;
    }

    const payload = {
      brand: "dr-hoffmann",
      requestedOfferType: "career-application",
      reason: "karriere",
      notificationRecipients: ["karen@mediconsult.group", "info@mediconsult.group"],
      name: `${form.firstName.trim()} ${form.lastName.trim()}`.trim(),
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      desiredModel: form.desiredModel,
      message: form.message.trim(),
      documentNames: documents.map((file) => file.name),
      documents,
      ...tracking,
    };

    setSubmitting(true);
    try {
      const result = await submitToBackend(payload);
      if (
        result?.ok ||
        result?.success ||
        result?.status === 200 ||
        result?.status === 201
      ) {
        setShowSuccess(true);
        setForm({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          desiredModel: "Minijob",
          message: "",
          acceptedContact: false,
        });
        setDocuments([]);
        setDocumentInputKey((current) => current + 1);
      } else {
        throw new Error(
          result?.message || "Bewerbung konnte nicht verarbeitet werden."
        );
      }
    } catch (err) {
      const errorMsg = err?.message || String(err);
      if (/offline|network|econnrefused/i.test(errorMsg)) {
        setError(
          "Die Bewerbung konnte gerade nicht übermittelt werden. Bitte versuche es gleich erneut."
        );
      } else {
        setError(
          errorMsg || "Die Bewerbung konnte gerade nicht verarbeitet werden. Bitte versuche es gleich erneut."
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout bodyClass="career-page-body">
      <section className="career-hero">
        <div className="career-shell career-hero__grid">
          <div className="career-hero__content">
            <span className="career-kicker">Wir stellen ein</span>
            <h1>
              Karriere bei <span className="career-title-nowrap">Dr. Hoffmann</span>
            </h1>
            <p className="career-hero__subtitle">
              Wir stellen ein - flexibel, modern und mit Perspektive.
            </p>
            <p>
              Dr. Hoffmann steht für ein schnelles, smartes und skalierbares
              Gesundheitskonzept. Für den weiteren Aufbau unseres Netzwerks suchen
              wir engagierte Fachkräfte, zuverlässige Mitarbeiter und motivierte
              Partner, die gemeinsam mit uns neue Wege in der medizinischen
              Versorgung gehen möchten.
            </p>
            <a className="btn-primary" href="#bewerben">
              Jetzt bewerben
            </a>
          </div>
          <aside className="career-hero__panel" aria-label="Karriereoptionen">
            <strong>Flexible Einstiegsmodelle</strong>
            <span>Minijob</span>
            <span>Teilzeit</span>
            <span>Festanstellung</span>
            <span>Lizenznehmer / Partner</span>
          </aside>
        </div>
      </section>

      <section className="career-section">
        <div className="career-shell career-intro">
          <span className="career-kicker">Verstärkung gesucht</span>
          <h2>Wir suchen Verstärkung</h2>
          <p>
            Dr. Hoffmann wächst - und dafür suchen wir Menschen, die Verantwortung
            übernehmen, zuverlässig arbeiten und Freude daran haben, Teil eines
            modernen Gesundheitskonzepts zu sein.
          </p>
          <p>
            Ob als Minijob, in Teilzeit, in Festanstellung oder als selbstständiger
            Lizenznehmer: Bei Dr. Hoffmann gibt es unterschiedliche Möglichkeiten,
            sich einzubringen. Wichtig ist uns vor allem eine professionelle
            Arbeitsweise, ein freundliches Auftreten und die Bereitschaft, gemeinsam
            mit uns Qualität, Service und Struktur in der Gesundheitsversorgung
            weiterzuentwickeln.
          </p>
          <p>
            Wir suchen insbesondere Fachkräfte und Facharbeiter, die im
            medizinischen, pflegerischen, organisatorischen oder serviceorientierten
            Bereich Erfahrung mitbringen. Auch Quereinsteiger mit hoher Motivation,
            Verantwortungsbewusstsein und Lernbereitschaft können für bestimmte
            Aufgabenbereiche interessant sein.
          </p>
        </div>
      </section>

      <section className="career-section career-section--muted">
        <div className="career-shell">
          <div className="career-section__head">
            <span className="career-kicker">Modelle</span>
            <h2>Beschäftigungsmodelle</h2>
          </div>
          <div className="career-card-grid">
            {EMPLOYMENT_MODELS.map((model) => (
              <article className="career-card" key={model.title}>
                <h3>{model.title}</h3>
                <p>{model.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="career-section">
        <div className="career-shell career-two-column">
          <div>
            <span className="career-kicker">Profil</span>
            <h2>Wen wir suchen</h2>
            <p>
              Wir suchen Menschen, die verbindlich arbeiten, gern mit Menschen
              umgehen und sich in einem strukturierten Gesundheitsumfeld
              weiterentwickeln möchten.
            </p>
            <ul className="career-check-list">
              {PROFILE_POINTS.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          </div>
          <div className="career-side-box">
            <h3>Besonders interessant sind Bewerber aus folgenden Bereichen:</h3>
            <ul className="career-pill-list">
              {EXPERIENCE_AREAS.map((area) => (
                <li key={area}>{area}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="career-section career-section--muted">
        <div className="career-shell career-two-column career-two-column--center">
          <div>
            <span className="career-kicker">Perspektive</span>
            <h2>Was wir bieten</h2>
            <p>
              Bei Dr. Hoffmann erwartet dich ein professionelles Umfeld mit klaren
              Strukturen, persönlicher Verantwortung und realen Möglichkeiten,
              den Aufbau eines modernen Systems mitzugestalten.
            </p>
          </div>
          <ul className="career-check-list career-check-list--compact">
            {BENEFITS.map((benefit) => (
              <li key={benefit}>{benefit}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="career-apply" id="bewerben">
        <div className="career-shell career-apply__grid">
          <div>
            <span className="career-kicker">Bewerbung</span>
            <h2>
              Jetzt Teil von <span className="career-title-nowrap">Dr. Hoffmann</span> werden
            </h2>
            <p>
              Du möchtest bei Dr. Hoffmann mitarbeiten oder als Lizenznehmer Teil
              unseres Netzwerks werden? Dann freuen wir uns auf deine Kontaktaufnahme.
            </p>
          </div>

          <form className="career-form" onSubmit={handleSubmit}>
            {error && <div className="auth-alert is-error">{error}</div>}

            <div className="career-form__row">
              <div>
                <label htmlFor="career-first-name">Vorname *</label>
                <input
                  id="career-first-name"
                  required
                  value={form.firstName}
                  onChange={updateForm("firstName")}
                  autoComplete="given-name"
                />
              </div>
              <div>
                <label htmlFor="career-last-name">Nachname *</label>
                <input
                  id="career-last-name"
                  required
                  value={form.lastName}
                  onChange={updateForm("lastName")}
                  autoComplete="family-name"
                />
              </div>
            </div>

            <div className="career-form__row">
              <div>
                <label htmlFor="career-email">E-Mail *</label>
                <input
                  id="career-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={updateForm("email")}
                  autoComplete="email"
                />
              </div>
              <div>
                <label htmlFor="career-phone">Telefonnummer</label>
                <input
                  id="career-phone"
                  type="tel"
                  value={form.phone}
                  onChange={updateForm("phone")}
                  autoComplete="tel"
                />
              </div>
            </div>

            <div>
              <label htmlFor="career-model">Gewünschtes Modell *</label>
              <select
                id="career-model"
                required
                value={form.desiredModel}
                onChange={updateForm("desiredModel")}
              >
                {MODEL_OPTIONS.map((model) => (
                  <option key={model} value={model}>
                    {model}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="career-message">Nachricht</label>
              <textarea
                id="career-message"
                rows={5}
                value={form.message}
                onChange={updateForm("message")}
                placeholder="Erzähle uns kurz, welche Erfahrung du mitbringst und welches Modell dich interessiert."
              />
            </div>

            <div>
              <label htmlFor="career-documents">Dokumente hochladen</label>
              <div className="career-upload">
                <input
                  key={documentInputKey}
                  id="career-documents"
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.odt,.rtf,.txt,.jpg,.jpeg,.png"
                  onChange={updateDocuments}
                  disabled={documents.length >= 3}
                />
                <label className="career-upload__box" htmlFor="career-documents">
                  <span className="career-upload__icon" aria-hidden="true">+</span>
                  <span>
                    <strong>Dokumente auswählen</strong>
                    <small>Lebenslauf, Bewerbung oder Zeugnisse - bis zu 3 Dateien</small>
                  </span>
                </label>
              </div>
              {documents.length > 0 && (
                <ul className="career-file-list" aria-label="Ausgewählte Dokumente">
                  {documents.map((file) => (
                    <li key={`${file.name}-${file.size}`}>
                      <span>
                        <strong>{file.name}</strong>
                        <small>{Math.max(1, Math.round(file.size / 1024))} KB</small>
                      </span>
                      <button
                        type="button"
                        onClick={() => removeDocument(file)}
                        aria-label={`${file.name} entfernen`}
                      >
                        Entfernen
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {documents.length >= 3 && (
                <p className="career-upload__limit">Maximal 3 Dokumente ausgewählt.</p>
              )}
            </div>

            <label className="career-checkbox">
              <input
                type="checkbox"
                checked={form.acceptedContact}
                onChange={updateForm("acceptedContact")}
              />
              <span>
                Ich bin damit einverstanden, dass MediConsult meine Angaben zur
                Bearbeitung der Bewerbung verarbeitet und mich dazu kontaktiert.
              </span>
            </label>

            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? "Bewerbung wird gesendet..." : "Jetzt bewerben"}
            </button>
          </form>
        </div>
      </section>

      {showSuccess && (
        <div className="career-success-modal" role="dialog" aria-modal="true" aria-labelledby="career-success-title">
          <div className="career-success-modal__card">
            <span className="career-success-modal__mark" aria-hidden="true">✓</span>
            <h2 id="career-success-title">Bewerbung erhalten</h2>
            <p>
              Vielen Dank. Wir haben deine Daten empfangen und kümmern uns
              schnellstmöglich darum.
            </p>
            <button
              type="button"
              className="btn-primary"
              onClick={() => setShowSuccess(false)}
            >
              Schließen
            </button>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default KarrierePage;
